import React, { useState } from "react";
import { Button } from "@/ui-components/ui/button";
import { ImageUpload } from "@/ui-components/ui/image-upload";
import { AlertCircle, ImagePlus } from "lucide-react";
import { CreateBaseDialog } from "@/util-components/dialog/base-dialogs/create-base-dialog";

interface UploadGraphicDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onContinue: (file: File) => void;
}

export const UploadGraphicDialog = ({
  isOpen,
  onOpenChange,
  onContinue,
}: UploadGraphicDialogProps) => {
  const [file, setFile] = useState<File | null>(null);

  const handleContinue = () => {
    if (file) {
      onContinue(file);
      // Reset after continue
      setTimeout(() => setFile(null), 300);
    }
  };

  const handleOpenChange = (open: boolean) => {
    onOpenChange(open);
    if (!open) {
      setTimeout(() => setFile(null), 300);
    }
  };

  return (
    <CreateBaseDialog
      open={isOpen}
      onClose={() => handleOpenChange(false)}
      icon={ImagePlus}
      title="Eigene Grafik hochladen"
      description="Lade hier eine fertige Grafik (z.B. erstellt mit Photoshop, Canva oder Illustrator) hoch, um sie direkt zu posten."
      hideSubmitButton={true}
      maxWidth="lg"
      customFooterActions={
        <Button onClick={handleContinue} disabled={!file} className="h-11 px-6 sm:px-8 min-w-[140px] rounded-xl font-bold text-sm bg-blue-600 hover:bg-blue-700 text-white shadow-sm transition-all w-full sm:w-auto">
          Weiter
        </Button>
      }
    >
      <div className="flex-1 overflow-y-auto px-5 sm:px-6 pb-5 sm:pb-6 pt-2 space-y-6">
        <div className="flex justify-center w-full">
          <div className="w-full max-w-md mx-auto">
            <ImageUpload
              value={file}
              onChange={setFile}
              maxSizeInMB={15}
              imageLabel="Grafik hochladen"
              aspectRatio="auto"
              recommendedAspectRatio={null}
            />
          </div>
        </div>

        <div className="flex items-start gap-2 text-xs text-gray-500 bg-blue-50/50 p-3 rounded-lg border border-blue-100/50">
          <AlertCircle className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
          <p className="leading-relaxed">
            Die Grafik wird beim Posten automatisch komprimiert und auf eine optimale Größe skaliert (kürzeste Seite max. 1080px).
          </p>
        </div>
      </div>
    </CreateBaseDialog>
  );
};
